import React, { useState } from 'react';
import { Box, Typography, Link, Card, Stack, Chip, useTheme, Button, Divider } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddIcon from '@mui/icons-material/Add';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import DirectionsIcon from '@mui/icons-material/Directions';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import dayjs from 'dayjs';
import ContactHelpModal from 'components/organisms/contactHelpModal';
import { ActionButton } from './styled';
import { useProfile } from 'context';

const BookingMobileView = ({
  bookings,
  navigateBooking,
  navigateVenue,
  statusMapping,
  navigateSearch,
  resultLimit,
  expandNextBooking = false,
  handleCheckin,
  handleCheckout
}) => {
  const theme = useTheme();
  const { profile } = useProfile();
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const handleOpenHelpModal = (venue) => {
    setSelectedVenue(venue);
    setHelpModalOpen(true);
  };

  const openInGoogleMaps = (venue) => {
    if (venue?.google_maps_url) {
      window.open(venue.google_maps_url, '_blank');
    } else {
      const query = encodeURIComponent(`${venue.name || ''}, ${venue.address || ''}`);
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${query}`, '_blank');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
        mb: 2
      }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: '1.125rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <EventNoteIcon sx={{
            fontSize: '1.25rem',
            position: 'relative',
            top: '-1px'
          }} />
          Your Schedule
        </Typography>
        <Link
          onClick={navigateSearch}
          sx={{
            color: 'primary.main',
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          See all
        </Link>
      </Box>

      {/* Cards Container */}
      <Box sx={{ px: 2 }}>
        <Stack spacing={2}>
          {bookings?.slice(0, resultLimit).map((item, index) => {
            const isFirstBooking = index === 0;
            const showAdditionalInfo = expandNextBooking && isFirstBooking;

            const formattedStartTime = item?.planned_start_time ?
              dayjs(item.planned_start_time, 'HH:mm').format('HH:mm') : '';
            const formattedEndTime = item?.planned_end_time ?
              dayjs(item.planned_end_time, 'HH:mm').format('HH:mm') : '';
            const formattedDate = dayjs(item?.planned_start_day).format('DD MMM YYYY');
            const price = (parseFloat(item.price) || 0)// + (parseFloat(item.vat_amount) || 0);
            const formattedPrice = isNaN(price) ? '' : `£${price.toFixed(2)}`;

            // Opening hours formatting
            const bookingDate = dayjs(item?.planned_start_day);
            const dayOfWeek = bookingDate.format('dddd');
            const dayNumber = bookingDate.day() === 0 ? 6 : bookingDate.day() - 1;
            const dayOpeningHours = item?.venue?.open_time?.find(schedule =>
              schedule.day === dayNumber && schedule.status === "AC"
            );
            const formattedOpeningHours = dayOpeningHours
              ? `${dayOfWeek} opens from ${dayOpeningHours.time_start.substring(0, 5)} to ${dayOpeningHours.time_end.substring(0, 5)}`
              : `${dayOfWeek} - Contact venue for opening hours`;

            return (
              <Card
                key={index}
                sx={{
                  overflow: 'visible',
                  transition: 'all 0.2s ease',
                  boxShadow: theme.shadows[1],
                  borderRadius: 2
                }}
              >
                {/* Next Booking Info Section for first booking */}
                {showAdditionalInfo && (
                  <Box sx={{ p: 2, pb: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ mb: 2, color: 'primary.main' }}
                    >
                      🎯 Your next workspace is just {dayjs(item?.planned_start_day).diff(dayjs(), 'days')} days away, {profile?.first_name}!
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        navigateVenue(item?.venue?.slug);
                      }}
                    >
                      {item?.venue?.name}
                    </Typography>

                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.3 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {item?.venue?.single_address || 'Address not available'}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.3 }} />
                        <Typography variant="body2">
                          {formattedOpeningHours}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                        <MeetingRoomIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.3 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                            Access instructions:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item?.venue?.access || 'No special instructions provided.'}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <ActionButton
                          variant="outlined"
                          size="small"
                          fullWidth
                          startIcon={<DirectionsIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openInGoogleMaps(item?.venue);
                          }}
                        >
                          Get me there
                        </ActionButton>

                        <ActionButton
                          variant="outlined"
                          color="primary"
                          size="small"
                          fullWidth
                          startIcon={<PhoneIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenHelpModal(item?.venue);
                          }}
                        >
                          Need help
                        </ActionButton>
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <ActionButton
                          variant="outlined"
                          color={item.real_start ? "primary" : "success"}
                          size="small"
                          startIcon={<CheckIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCheckin && handleCheckin(item);
                          }}
                          disabled={!!item.real_start}
                          fullWidth
                        >
                          {item.real_start
                            ? `Checked in: ${dayjs(item.real_start).format('HH:mm')}`
                            : "Check-in"
                          }
                        </ActionButton>

                        <ActionButton
                          variant="outlined"
                          color={item.real_end ? "primary" : "error"}
                          size="small"
                          startIcon={<LogoutIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCheckout && handleCheckout(item);
                          }}
                          disabled={!!item.real_end || !item.real_start}
                          fullWidth
                        >
                          {item.real_end
                            ? `Checked out: ${dayjs(item.real_end).format('HH:mm')}`
                            : "Check-out"
                          }
                        </ActionButton>
                      </Stack>
                    </Stack>

                    <Divider sx={{ mt: 2, mb: 0 }} />
                  </Box>
                )}

                {/* Regular Booking Info */}
                <Box
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={() => navigateBooking(item)}
                >
                  <Stack spacing={2}>
                    {/* Venue and Status */}
                    {!showAdditionalInfo && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                          {item?.venue?.name}
                        </Typography>
                        <Chip
                          label={statusMapping[item?.status]}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            color: 'primary.main',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    )}

                    {/* Always show status chip for first booking with additional info */}
                    {showAdditionalInfo && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={statusMapping[item?.status]}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            color: 'primary.main',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    )}

                    {/* Date and Time */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formattedDate} {formattedStartTime && `• ${formattedStartTime} - ${formattedEndTime}`}
                      </Typography>
                    </Box>

                    {/* Price and Code */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 'auto'
                    }}>
                      <Typography
                        variant="body2"
                        color="primary.main"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight: 500
                        }}
                      >
                        {formattedPrice}
                      </Typography>
                      {item?.code && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          Code: {item.code}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Card>
            );
          })}

          {/* Empty State Card */}
          {bookings.length < 5 && (
            <Card
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                backgroundColor: 'grey.50',
                border: '2px dashed',
                borderColor: 'divider',
                boxShadow: 'none'
              }}
            >
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: 'primary.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}>
                <EventNoteIcon sx={{
                  fontSize: 30,
                  color: 'primary.main'
                }} />
              </Box>
              <Typography variant="h6" color="primary" gutterBottom>
                Find Your Next Space
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Discover perfect workspaces near you
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={navigateSearch}
                sx={{
                  marginTop: '20px',
                  borderRadius: '20px',
                  textTransform: 'none',
                  padding: '8px 24px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Explore Spaces
              </Button>
            </Card>
          )}
        </Stack>
      </Box>

      <ContactHelpModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        venue={selectedVenue}
      />
    </Box>
  );
};

export default BookingMobileView;